/**
 * adminApi.ts — typed client for the governed scoring-config admin API (T-368).
 *
 * Mirrors lib/api.ts: authedFetch attaches the Cognito token, and every helper throws on a
 * non-2xx so the caller decides how to surface it. The server's error message is carried
 * through verbatim — the governance refusals ("approved_by is required…") are written to be
 * shown to an operator, not swallowed into a generic "request failed".
 */
import { authedFetch } from './api'

const BASE = '/api/admin/scoring-config'

export type ConfigScope = 'site' | 'tract'
export type ConfigStatus = 'draft' | 'published' | 'archived'

export interface ConfigValue {
  scoreKey: string
  dominantType?: string | null
  component: string
  value: number
}

export interface ConfigVersion {
  id: number
  scope: ConfigScope
  version: number
  status: ConfigStatus
  active: boolean
  note: string | null
  createdBy: string
  createdAt: string
  approvedBy: string | null
  approvalRef: string | null
  publishedAt: string | null
}

export interface ConfigVersionWithValues extends ConfigVersion {
  values: ConfigValue[]
}

export interface SiteScoreTiers {
  d: { le010: number; le025: number; le050: number; le100: number; le200: number; gt200: number }
  f: { ge8: number; ge4: number; ge1: number; zero: number }
  m: {
    ge8: number
    ge5: number
    ge3: number
    ge1: number
    none: number
    networkBonus: number
    networkBonusMin: number
  }
  r: {
    mfhHigh: number
    mfhLow: number
    sfhHigh: number
    sfhLow: number
    mfhHccThreshold: number
    sfhHcoThreshold: number
  }
  competition: { dcfcWeight: number; l2Weight: number }
  radii: { poiMi: number; competitionMi: number }
}

export interface ActiveConfigResponse {
  configVersion: string
  active: (ConfigVersionWithValues & { versionId: number }) | null
  defaults: SiteScoreTiers
  resolved: SiteScoreTiers
}

export interface PreviewSampleRow {
  name: string
  description: string
  before: { siteLvi: number; adjustments: { d: number | null; f: number; m: number; r: number } }
  after: { siteLvi: number; adjustments: { d: number | null; f: number; m: number; r: number } }
  deltaLvi: number
}

export interface PreviewResponse {
  versionId: number
  version: number
  status: ConfigStatus
  samples: PreviewSampleRow[]
}

/** Surface the server's message (it is written for the operator), falling back to the status. */
async function unwrap<T>(res: Response, what: string): Promise<T> {
  if (!res.ok) {
    let message = `${what} failed: ${res.status}`
    try {
      const body = (await res.json()) as { error?: string }
      if (body?.error) message = body.error
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new Error(message)
  }
  return res.json() as Promise<T>
}

// A bodyless POST must NOT declare a JSON content-type: Fastify rejects "content-type is
// application/json but the body is empty" with a 400. (Found in live UAT — server.inject in
// the API tests sends no content-type at all, so the mismatch was invisible there.)
const jsonInit = (method: string, body?: unknown): RequestInit =>
  body === undefined
    ? { method }
    : { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }

export async function fetchActiveConfig(
  scope: ConfigScope = 'site',
): Promise<ActiveConfigResponse> {
  return unwrap(await authedFetch(`${BASE}/active?scope=${scope}`), 'Load active config')
}

export async function fetchVersions(scope: ConfigScope = 'site'): Promise<ConfigVersion[]> {
  const body = await unwrap<{ versions: ConfigVersion[] }>(
    await authedFetch(`${BASE}/versions?scope=${scope}`),
    'Load versions',
  )
  return body.versions
}

export async function createDraft(scope: ConfigScope, note: string): Promise<ConfigVersion> {
  return unwrap(
    await authedFetch(`${BASE}/versions`, jsonInit('POST', { scope, note })),
    'Create draft',
  )
}

export async function saveDraftValues(
  id: number,
  values: ConfigValue[],
): Promise<ConfigVersionWithValues> {
  return unwrap(
    await authedFetch(`${BASE}/versions/${id}/values`, jsonInit('PUT', { values })),
    'Save draft',
  )
}

export async function previewVersion(id: number): Promise<PreviewResponse> {
  return unwrap(await authedFetch(`${BASE}/versions/${id}/preview`, jsonInit('POST')), 'Preview')
}

export async function publishVersion(
  id: number,
  signoff: { approvedBy: string; approvalRef: string },
): Promise<ConfigVersion> {
  return unwrap(
    await authedFetch(`${BASE}/versions/${id}/publish`, jsonInit('POST', signoff)),
    'Publish',
  )
}

export async function activateVersion(id: number): Promise<ConfigVersion> {
  return unwrap(await authedFetch(`${BASE}/versions/${id}/activate`, jsonInit('POST')), 'Activate')
}

// ─── Tier ⇄ value-row mapping (shared by the editor and the diff view) ─────────

const FAMILY_KEYS: Array<[keyof SiteScoreTiers, string]> = [
  ['d', 'D'],
  ['f', 'F'],
  ['m', 'M'],
  ['r', 'R'],
  ['competition', 'competition_subweights'],
  ['radii', 'sub_radii'],
]

/** Flatten an edited tier object into the store's value rows, omitting anything unchanged. */
export function tiersToValues(edited: SiteScoreTiers, baseline: SiteScoreTiers): ConfigValue[] {
  const rows: ConfigValue[] = []
  for (const [family, scoreKey] of FAMILY_KEYS) {
    const editedFamily = edited[family] as unknown as Record<string, number>
    const baseFamily = baseline[family] as unknown as Record<string, number>
    for (const [component, value] of Object.entries(editedFamily)) {
      if (value !== baseFamily[component]) rows.push({ scoreKey, component, value })
    }
  }
  return rows
}

/** Every knob as a flat list, for rendering the editor in a stable order. */
export function tierFields(
  tiers: SiteScoreTiers,
): Array<{ family: keyof SiteScoreTiers; component: string; value: number; label: string }> {
  const out: Array<{
    family: keyof SiteScoreTiers
    component: string
    value: number
    label: string
  }> = []
  for (const [family, scoreKey] of FAMILY_KEYS) {
    for (const [component, value] of Object.entries(
      tiers[family] as unknown as Record<string, number>,
    )) {
      out.push({ family, component, value, label: `${scoreKey}.${component}` })
    }
  }
  return out
}
